import type {Component, Editor, ProjectData} from 'grapesjs';
import {FORM_MODE_MODEL_KEY, FormMakerFormMode} from '@FormMakerReact/form-maker.types';
import {initializeEditor} from '@FormMakerReact/grapesjs-form-maker-plugin/main';
import {asyncSleep} from '@Utils/misc.util';
import {POSTERMYWALL_FORM_BACKGROUND_TYPE} from '@FormMakerReact/components/postermywall-form-background/postermywall-form-background';
import {POSTERMYWALL_SIGNUP_FORM_TYPE} from '@FormMakerReact/components/postermywall-signup-form/postermywall-signup-form';
import {POSTERMYWALL_CONFIRMATION_FORM_TYPE} from '@FormMakerReact/components/postermywall-confirmation-form/postermywall-confirmation-form';

export const PMW_CANVAS_HIDDEN_CLASS = 'pmw-canvas-hidden';

export const PMW_CANVAS_VISIBLE_CLASS = 'pmw-canvas-visible';

const PAGE_PRELOADER_SELECTOR = '.js-form-maker-preloader';

const PAGE_PRELOADER_FADE_MS = 800;

const PAGE_PRELOADER_TIP_SELECTOR = '.js-loading-tip';

const PAGE_PRELOADER_VISIBLE_TIP_CLASS = '-visible';

const PAGE_PRELOADER_TIP_CYCLE_MS = 5000;

export interface MountEditorArgs {
  container: HTMLElement;
}

export function startFormMakerPreloaderTipRotation(): VoidFunction {
  const intervalId = window.setInterval(() => {
    const preloader = document.querySelector(PAGE_PRELOADER_SELECTOR);
    const displayedTip = preloader?.querySelector(`${PAGE_PRELOADER_TIP_SELECTOR}.${PAGE_PRELOADER_VISIBLE_TIP_CLASS}`);

    if (!displayedTip) {
      window.clearInterval(intervalId);
      return;
    }

    const nextTip = displayedTip.nextElementSibling;

    if (nextTip instanceof HTMLElement && nextTip.matches(PAGE_PRELOADER_TIP_SELECTOR)) {
      displayedTip.classList.remove(PAGE_PRELOADER_VISIBLE_TIP_CLASS);
      nextTip.classList.add(PAGE_PRELOADER_VISIBLE_TIP_CLASS);
    } else {
      window.clearInterval(intervalId);
    }
  }, PAGE_PRELOADER_TIP_CYCLE_MS);

  return (): void => {
    window.clearInterval(intervalId);
  };
}

export function hideFormMakerPagePreloader(): void {
  const preloader = document.querySelector(PAGE_PRELOADER_SELECTOR);
  if (!(preloader instanceof HTMLElement)) {
    return;
  }

  preloader.style.transition = `opacity ${PAGE_PRELOADER_FADE_MS}ms ease`;
  preloader.style.opacity = '0';

  window.setTimeout(() => {
    preloader.remove();
  }, PAGE_PRELOADER_FADE_MS);
}

export function mountEditor({container}: MountEditorArgs): Editor {
  return initializeEditor(container);
}

export function destroyEditor(editor: Editor): void {
  editor.destroy();
}

export function waitForEditorLoad(editor: Editor): Promise<void> {
  return new Promise((resolve) => {
    editor.onReady(() => {
      resolve();
    });
  });
}

/**
 * GrapesJS's `load` (onReady) fires before the canvas iframe has rendered the component views.
 * Resolves once the frame body has rendered — immediately when it already has.
 */
export function waitForCanvasFrameRendered(editor: Editor): Promise<void> {
  if (editor.Canvas.getFrame().view?.loaded) {
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    editor.once('canvas:frame:load:body', () => {
      resolve();
    });
  });
}

/** GrapesJS books every model change on its dirty count through a zero-delay timer (EditorModel.handleUpdates); the next macrotask is when the pending ones have landed. */
export function waitForDirtyCountToSettle(): Promise<void> {
  return asyncSleep(0);
}

export function loadFormIntoEditor(editor: Editor, idEmailSignupForm: string): Promise<ProjectData> {
  return editor.load({idEmailSignupForm});
}

export function selectFormBackgroundForTab(editor: Editor, mode: FormMakerFormMode): void {
  const wrapper = editor.getWrapper();
  if (!wrapper) {
    return;
  }
  const targetParentType = mode === FormMakerFormMode.SIGNUP ? POSTERMYWALL_SIGNUP_FORM_TYPE : POSTERMYWALL_CONFIRMATION_FORM_TYPE;
  const matches = wrapper.findType(POSTERMYWALL_FORM_BACKGROUND_TYPE);
  const match = matches.find((bg: Component): boolean => bg.parent()?.get('type') === targetParentType);
  if (match) {
    editor.select(match);
  }
}

export function toggleFormVisibility(editor: Editor, mode: FormMakerFormMode): void {
  const wrapper = editor.getWrapper();
  if (!wrapper) {
    return;
  }
  const signupForm = wrapper.findType(POSTERMYWALL_SIGNUP_FORM_TYPE)[0];
  const confirmationForm = wrapper.findType(POSTERMYWALL_CONFIRMATION_FORM_TYPE)[0];
  if (!signupForm || !confirmationForm) {
    return;
  }
  if (mode === FormMakerFormMode.SIGNUP) {
    applyVisibilityClassWhenRendered(signupForm, true);
    applyVisibilityClassWhenRendered(confirmationForm, false);
  } else {
    applyVisibilityClassWhenRendered(signupForm, false);
    applyVisibilityClassWhenRendered(confirmationForm, true);
  }
  editor.getModel().set(FORM_MODE_MODEL_KEY, mode);
  selectFormBackgroundForTab(editor, mode);
}

function applyVisibilityClassWhenRendered(component: Component, visible: boolean): void {
  const el = component.getEl();
  if (!el) {
    return;
  }
  if (visible) {
    el.classList.add(PMW_CANVAS_VISIBLE_CLASS);
    el.classList.remove(PMW_CANVAS_HIDDEN_CLASS);
  } else {
    el.classList.add(PMW_CANVAS_HIDDEN_CLASS);
    el.classList.remove(PMW_CANVAS_VISIBLE_CLASS);
  }
}
